import React, { useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet";

import './Event.css';
import Button from "../../components/button/Button";

type EventFormData = {
	email: string;
	message: string;
};

type EventToastState = {
	message: string;
	variant: 'success' | 'danger' | 'secondary';
	show: boolean;
	isExiting: boolean;
};

export const Event: React.FC = (): JSX.Element => {
	const [formData, setFormData] = useState<EventFormData>({
		email: '',
		message: ''
	});

	const [validationErrors, setValidationErrors] = useState<EventFormData>({
		email: '',
		message: ''
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [toastState, setToastState] = useState<EventToastState>({
		message: '',
		variant: 'secondary',
		show: false,
		isExiting: false
	});
	const exitTimerRef = useRef<number | undefined>(undefined);
	const hideTimerRef = useRef<number | undefined>(undefined);

	const clearToastTimers = () => {
		if (exitTimerRef.current !== undefined) {
			window.clearTimeout(exitTimerRef.current);
		}

		if (hideTimerRef.current !== undefined) {
			window.clearTimeout(hideTimerRef.current);
		}
	};

	const hideToast = () => {
		clearToastTimers();
		setToastState(prevState => ({
			...prevState,
			show: false,
			isExiting: false
		}));
	};

	const startToastExit = () => {
		clearToastTimers();
		setToastState(prevState => ({
			...prevState,
			isExiting: true
		}));

		hideTimerRef.current = window.setTimeout(() => {
			hideToast();
		}, 220);
	};

	const showToast = (message: string, variant: 'success' | 'danger' | 'secondary') => {
		clearToastTimers();
		setToastState({
			message,
			variant,
			show: true,
			isExiting: false
		});

		exitTimerRef.current = window.setTimeout(() => {
			startToastExit();
		}, 2600);
	};

	useEffect(() => {
		return () => {
			clearToastTimers();
		};
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData(prevState => ({
			...prevState,
			[name]: value
		}));
	};

	const validateForm = () => {
		const errors = {
			email: '',
			message: ''
		};

		if (!formData.email) {
			errors.email = 'Email is required.';
		}

		if (!formData.message) {
			errors.message = 'Message is required.';
		}

		setValidationErrors(errors);

		return Object.values(errors).every(err => !err);
	};

	const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formElement = e.currentTarget;

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		showToast('Sending...', 'secondary');

		const formDataToSubmit = new FormData(formElement);
		formDataToSubmit.append('access_key', '3a51689e-01fb-4dc9-88da-2bfc39475e9e');
		formDataToSubmit.append('subject', 'Hallwylska Event Form');
		formDataToSubmit.append('from_name', 'Hallwylska Event Page');

		try {
			const response = await fetch('https://api.web3forms.com/submit', {
				method: 'POST',
				body: formDataToSubmit
			});

			let data: { success?: boolean | string; message?: string } | null = null;

			try {
				data = await response.json();
			} catch {
				data = null;
			}

			const isSuccess = response.ok && (data === null || data.success === true || data.success === 'true');

			if (isSuccess) {
				showToast('Form submitted successfully', 'success');
				formElement.reset();
				setFormData({
					email: '',
					message: ''
				});
			} else {
				showToast(data?.message || 'Error', 'danger');
			}
		} catch {
			showToast('Error', 'danger');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Helmet>
                <title>Events - Hallwylska Restaurang</title>
                <meta name="description" content="Plan your perfect event with Hallwylska Restaurang's expert team. From corporate gatherings to family celebrations, we cater to all your needs." />
                <link rel="canonical" href="https://hallwylskarestaurang.com/event" />
                <meta property="og:title" content="Events - Hallwylska Restaurang" />
                <meta property="og:description" content="Plan your perfect event with Hallwylska Restaurang's expert team. From corporate gatherings to family celebrations, we cater to all your needs." />
                <meta property="og:url" content="https://hallwylskarestaurang.com/event" />
                <meta name="twitter:title" content="Events - Hallwylska Restaurang" />
                <meta name="twitter:description" content="Plan your perfect event with Hallwylska Restaurang's expert team. From corporate gatherings to family celebrations, we cater to all your needs." />
            </Helmet>
			<section className="event beige-bg pt-5 pb-5">
				<div className="event-toast-container">
					{toastState.show && (
						<div
							className={`event-toast event-toast-${toastState.variant} ${toastState.isExiting ? 'event-toast-exit' : 'event-toast-enter'}`}
							role="status"
							aria-live="polite"
							aria-atomic="true"
						>
							<span>{toastState.message}</span>
							<button
								type="button"
								className="event-toast-close"
								aria-label="Close"
								onClick={startToastExit}
							>
								x
							</button>
						</div>
					)}
				</div>
				<div className="container">
					<div className="row">
						<div className="col-lg-3">
							<img src="/img/event1.webp" alt="Hand holding a glass" className="event-image-1" />
						</div>
						<div className="col-lg-6">
							<h1 className="text-green">Event</h1>
							<p>
								Whether you're planning a corporate gathering, a family celebration, or a special get-together with friends, we believe that your event should be a reflection of you. Our team of dedicated event specialists is here to transform your vision into a reality, ensuring that every detail is tailored to perfection.
							</p>
							<p>
								From handpicked menu selections that tantalize your taste buds to meticulously designed décor that mirrors your style, we're committed to crafting a memorable event that leaves a lasting impression on you and your guests.
							</p>
							<p>
								Start curating your event, simply fill out the form below. Share your preferences, ideas, and requirements, and we'll get in touch with you to discuss how we can bring your vision to life.
							</p>

							{/* New Form */}
							<form onSubmit={handleFormSubmit}>
								<div className={`mb-3 ${validationErrors.email ? 'has-validation' : ''}`}>
									<label htmlFor="email" className="form-label">Email</label>
									<input	type="email"
											className={`form-control box-3d ${validationErrors.email ? 'is-invalid' : ''}`}
											id="email"
											name="email"
											placeholder="Email"
											value={formData.email}
											onChange={handleInputChange} />
									{validationErrors.email && <div className="invalid-feedback">{validationErrors.email}</div>}
								</div>

								<div className={`mb-3 ${validationErrors.message ? 'has-validation' : ''}`}>
									<label htmlFor="message" className="form-label">Message</label>
									<textarea className={`form-control box-3d ${validationErrors.message ? 'is-invalid' : ''}`}
											id="message"
											placeholder="Type your message here ..."
											rows={4}
											name="message"
											value={formData.message}
											onChange={handleInputChange}></textarea>
									{validationErrors.message && <div className="invalid-feedback">{validationErrors.message}</div>}
								</div>
								<p className="text-center">
									<Button class_type="primary" text={isSubmitting ? 'Sending...' : 'Submit'} type="submit" />
								</p>
							</form>
						</div>
						<div className="col-lg-3">
							<img src="/img/event2.webp" alt="Two legs with shoes saying Bienvenue" className="event-image-2" />
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
